import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  SKIP_AUTH,
  clearAdminToken,
  createCatalogItem,
  deleteCatalogItem,
  devLogin,
  getAdminCatalog,
  getStoredAdminToken,
  googleAuth,
  reorderCatalogItems,
  storeAdminToken,
  updateCatalogItem,
} from '../api/catalog';
import { GoogleLoginButton } from '../components/GoogleLoginButton';
import { Header } from '../components/Header';
import type { CatalogFormValues, CatalogItem, User } from '../types';

const emptyForm: CatalogFormValues = {
  slug: '',
  name: '',
  subtitle: '',
  description: '',
  long_description: '',
  features: [],
  tags: [],
  image_url: '',
  icon: '',
  cta_label: '',
  cta_url: '',
  external: true,
  sort_order: 0,
  is_active: true,
};

function splitLines(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function joinList(values?: string[]) {
  return (values || []).join('\n');
}

function normalizeForm(form: CatalogFormValues): CatalogFormValues {
  return {
    ...form,
    slug: form.slug.trim(),
    name: form.name.trim(),
    subtitle: form.subtitle?.trim() || undefined,
    description: form.description?.trim() || undefined,
    long_description: form.long_description?.trim() || undefined,
    image_url: form.image_url?.trim() || undefined,
    icon: form.icon?.trim() || undefined,
    cta_label: form.cta_label?.trim() || undefined,
    cta_url: form.cta_url?.trim() || undefined,
    sort_order: Number(form.sort_order || 0),
    external: Boolean(form.external),
    is_active: Boolean(form.is_active),
  };
}

interface ItemFormProps {
  title: string;
  initialValue: CatalogFormValues;
  submitLabel: string;
  onSubmit: (values: CatalogFormValues) => Promise<void>;
  onCancel?: () => void;
}

function ItemForm({ title, initialValue, submitLabel, onSubmit, onCancel }: ItemFormProps) {
  const [form, setForm] = useState<CatalogFormValues>(initialValue);
  const [featuresText, setFeaturesText] = useState(joinList(initialValue.features));
  const [tagsText, setTagsText] = useState((initialValue.tags || []).join(', '));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setForm(initialValue);
    setFeaturesText(joinList(initialValue.features));
    setTagsText((initialValue.tags || []).join(', '));
  }, [initialValue]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setIsSaving(true);
    try {
      await onSubmit(normalizeForm({ ...form, features: splitLines(featuresText), tags: splitLines(tagsText) }));
      if (submitLabel === 'Create item') {
        setForm(emptyForm);
        setFeaturesText('');
        setTagsText('');
      }
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form data-testid={submitLabel === 'Create item' ? 'admin-new-form' : undefined} onSubmit={handleSubmit} className="card space-y-5 p-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-slate-950">{title}</h2>
        {onCancel && <button type="button" onClick={onCancel} className="text-sm font-semibold text-slate-500 hover:text-slate-900">Cancel</button>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label><span className="label">Slug</span><input required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" className="input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></label>
        <label><span className="label">Name</span><input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label><span className="label">Subtitle</span><input className="input" value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></label>
        <label><span className="label">Icon</span><input className="input" value={form.icon || ''} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></label>
        <label><span className="label">CTA label</span><input className="input" value={form.cta_label || ''} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} /></label>
        <label><span className="label">CTA URL</span><input className="input" value={form.cta_url || ''} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} /></label>
        <label><span className="label">Image URL</span><input className="input" value={form.image_url || ''} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></label>
        <label><span className="label">Sort order</span><input type="number" className="input" value={form.sort_order ?? 0} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} /></label>
      </div>

      <label><span className="label">Description</span><textarea className="input min-h-24" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
      <label><span className="label">Long description</span><textarea className="input min-h-32" value={form.long_description || ''} onChange={(e) => setForm({ ...form, long_description: e.target.value })} /></label>
      <label><span className="label">Features (comma or newline separated)</span><textarea className="input min-h-24" value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} /></label>
      <label><span className="label">Tags (comma separated)</span><input className="input" value={tagsText} onChange={(e) => setTagsText(e.target.value)} /></label>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700"><input type="checkbox" checked={form.external ?? false} onChange={(e) => setForm({ ...form, external: e.target.checked })} /> External CTA</label>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700"><input type="checkbox" checked={form.is_active ?? false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
      </div>

      <button data-testid="admin-save" type="submit" disabled={isSaving} className="btn-primary">
        {isSaving ? 'Saving…' : submitLabel}
      </button>
    </form>
  );
}

function toFormValue(item: CatalogItem): CatalogFormValues {
  return {
    slug: item.slug,
    name: item.name,
    subtitle: item.subtitle || '',
    description: item.description || '',
    long_description: item.long_description || '',
    features: item.features || [],
    tags: item.tags || [],
    image_url: item.image_url || '',
    icon: item.icon || '',
    cta_label: item.cta_label || '',
    cta_url: item.cta_url || '',
    external: item.external,
    sort_order: item.sort_order,
    is_active: item.is_active,
  };
}

export function AdminPage() {
  const [token, setToken] = useState<string | null>(() => getStoredAdminToken());
  const [user, setUser] = useState<User | null>(null);
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);

  const isAuthorized = Boolean(token && (!user || user.is_platform_admin));
  const editingItem = useMemo(() => items.find((item) => item.slug === editingSlug), [editingSlug, items]);

  const loadItems = useCallback(async (authToken: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAdminCatalog(authToken);
      setItems(data);
    } catch (err) {
      console.error('Failed to load admin catalog:', err);
      setError('Failed to load admin catalog. Please log in again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadItems(token);
  }, [token, loadItems]);

  async function finishLogin(response: Awaited<ReturnType<typeof devLogin>>) {
    if (!response.access_token) {
      throw new Error('Login response did not include an access token');
    }
    storeAdminToken(response.access_token);
    setToken(response.access_token);
    setUser(response.user);
  }

  async function handleGoogleSuccess(googleAccessToken: string) {
    try {
      setError(null);
      await finishLogin(await googleAuth(googleAccessToken));
    } catch (err) {
      console.error('Google login failed:', err);
      setError('Login failed. Please try again.');
    }
  }

  function handleGoogleError() {
    setError('Google login failed. Please try again.');
  }

  async function handleDevLogin() {
    try {
      setError(null);
      await finishLogin(await devLogin());
    } catch (err) {
      console.error('Dev login failed:', err);
      setError('Dev login failed. Is the API running with VITE_SKIP_AUTH=true?');
    }
  }

  function logout() {
    clearAdminToken();
    setToken(null);
    setUser(null);
    setItems([]);
    setEditingSlug(null);
  }

  async function refresh() {
    if (token) await loadItems(token);
  }

  async function handleCreate(values: CatalogFormValues) {
    if (!token) return;
    await createCatalogItem(token, values);
    await refresh();
  }

  async function handleEdit(values: CatalogFormValues) {
    if (!token || !editingSlug) return;
    await updateCatalogItem(token, editingSlug, values);
    setEditingSlug(null);
    await refresh();
  }

  async function handleToggle(item: CatalogItem) {
    if (!token) return;
    await updateCatalogItem(token, item.slug, { is_active: !item.is_active });
    await refresh();
  }

  async function moveItem(index: number, direction: -1 | 1) {
    if (!token) return;
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const next = [...items].sort((a, b) => a.sort_order - b.sort_order);
    const currentOrder = next[index].sort_order;
    next[index] = { ...next[index], sort_order: next[nextIndex].sort_order };
    next[nextIndex] = { ...next[nextIndex], sort_order: currentOrder };
    const ordered = [...next].sort((a, b) => a.sort_order - b.sort_order);
    setItems(ordered);
    const updated = await reorderCatalogItems(token, {
      items: ordered.map((item) => ({ id: item.id, sort_order: item.sort_order })),
    });
    setItems(updated);
  }

  async function handleDelete(item: CatalogItem) {
    if (!token) return;
    if (!confirm(`Delete ${item.name}?`)) return;
    await deleteCatalogItem(token, item.slug);
    await refresh();
  }

  if (!token) {
    return (
      <div data-testid="admin-root" className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-md px-4 py-16">
          <div data-testid="admin-login" className="card p-8 text-center">
            <h1 className="text-2xl font-bold text-slate-950">Catalog admin</h1>
            <p className="mt-3 text-slate-600">Sign in as a platform admin to manage public catalog items.</p>
            {SKIP_AUTH ? (
              <button
                data-testid="admin-login-button"
                type="button"
                onClick={handleDevLogin}
                className="btn-primary mt-8 w-full"
              >
                Dev login
              </button>
            ) : (
              <GoogleLoginButton onSuccess={handleGoogleSuccess} onError={handleGoogleError} />
            )}
            {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
          </div>
        </main>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div data-testid="admin-root" className="min-h-screen bg-slate-50">
        <Header />
        <main data-testid="admin-forbidden" className="mx-auto max-w-md px-4 py-16 text-center">
          <div className="card p-8">
            <h1 className="text-2xl font-bold text-slate-950">Not authorized</h1>
            <p className="mt-3 text-slate-600">Your account is not a platform admin.</p>
            <button data-testid="admin-logout" onClick={logout} className="btn-secondary mt-8">Logout</button>
          </div>
        </main>
      </div>
    );
  }

  const sortedItems = [...items].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div data-testid="admin-root" className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-6xl space-y-8 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-600">Admin</p>
            <h1 className="mt-2 text-3xl font-bold text-slate-950">Public catalog</h1>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="btn-secondary">Logout</button>
        </div>

        {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>}
        {isLoading && <div className="card p-6 text-slate-600">Loading…</div>}

        <ItemForm title="New item" initialValue={emptyForm} submitLabel="Create item" onSubmit={handleCreate} />

        {editingItem && (
          <ItemForm title={`Edit ${editingItem.name}`} initialValue={toFormValue(editingItem)} submitLabel="Save changes" onSubmit={handleEdit} onCancel={() => setEditingSlug(null)} />
        )}

        <section className="card overflow-hidden">
          <div className="border-b border-slate-200 p-5">
            <h2 className="text-xl font-bold text-slate-950">All items</h2>
          </div>
          <div className="divide-y divide-slate-200">
            {sortedItems.length === 0 && <p className="p-6 text-slate-600">No catalog items yet.</p>}
            {sortedItems.map((item, index) => (
              <div key={item.id} data-testid={`admin-item-${item.slug}`} className="grid gap-4 p-5 lg:grid-cols-[1fr_auto] lg:items-center">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-bold text-slate-950" dir="auto">{item.name}</h3>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">/{item.slug}</span>
                    <span className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-700">order {item.sort_order}</span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${item.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                  {item.description && <p className="mt-2 text-sm text-slate-600" dir="auto">{item.description}</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => moveItem(index, -1)} className="btn-secondary px-3" disabled={index === 0}>↑</button>
                  <button onClick={() => moveItem(index, 1)} className="btn-secondary px-3" disabled={index === sortedItems.length - 1}>↓</button>
                  <button onClick={() => handleToggle(item)} className="btn-secondary">{item.is_active ? 'Deactivate' : 'Activate'}</button>
                  <button onClick={() => setEditingSlug(item.slug)} className="btn-secondary">Edit</button>
                  <button onClick={() => handleDelete(item)} className="btn-danger">Delete</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
